import { useCallback } from 'react';
import { toast } from 'sonner';
import type { Workflow, WorkflowStage } from '@/components/ApprovalStageWizard/types';
import { ApiError } from '@/services/apiClient';
import * as workflowService from '@/services/workflowService';
import { buildUpdateRequest } from '../utils';

export interface UseWorkflowStageMutationsParams {
  isLive: boolean;
  selectedWorkflowId: string | null;
  workflows: Workflow[];
  setWorkflows: React.Dispatch<React.SetStateAction<Workflow[]>>;
  loadWorkflows: () => Promise<void>;
}

export interface UseWorkflowStageMutationsResult {
  handleDeleteStage: (stageId: string) => Promise<void>;
  handleReorderStages: (reorderedStages: WorkflowStage[]) => Promise<void>;
  handleDuplicateStageInWorkflow: (stageId: string) => Promise<void>;
}

/**
 * Owns the stage-list mutations (delete, reorder, duplicate) and the shared
 * persistence helper used by them.
 */
export const useWorkflowStageMutations = ({
  isLive,
  selectedWorkflowId,
  workflows,
  setWorkflows,
  loadWorkflows,
}: UseWorkflowStageMutationsParams): UseWorkflowStageMutationsResult => {
  // Helper to persist workflow to backend and handle errors
  const persistWorkflowUpdate = useCallback(async (
    workflowId: string,
    updatedWorkflow: Workflow,
    successMessage: string
  ) => {
    if (isLive) {
      try {
        const updateRequest = buildUpdateRequest(updatedWorkflow);
        await workflowService.updateWorkflow(workflowId, updateRequest);
        toast.success(successMessage);

        // Reload workflow to get canonical IDs from backend (especially for new stages)
        const hasNewStages = updatedWorkflow.stages.some((s) => s.id.startsWith('temp-') || s.id.startsWith('placeholder-'));
        if (hasNewStages) {
          await loadWorkflows();
        }
      } catch (err) {
        console.error('Failed to update workflow:', err);
        if (err instanceof ApiError) {
          toast.error(err.message);
        } else {
          toast.error('Failed to save changes to server');
        }
        // Revert local state on error
        loadWorkflows();
      }
    } else {
      toast.success(successMessage);
    }
  }, [isLive, loadWorkflows]);

  const handleDeleteStage = useCallback(async (stageId: string) => {
    if (!selectedWorkflowId) return;

    const currentWorkflow = workflows.find((w) => w.id === selectedWorkflowId);
    if (!currentWorkflow) return;

    const stageToDelete = currentWorkflow.stages.find((s) => s.id === stageId);
    if (!stageToDelete) return;

    // Build the updated workflow with the stage removed and orders renumbered
    const updatedStages = currentWorkflow.stages
      .filter((s) => s.id !== stageId)
      .map((s, index) => ({ ...s, order: index + 1 }));

    const updatedWorkflow: Workflow = {
      ...currentWorkflow,
      stages: updatedStages,
      updatedAt: new Date(),
    };

    // Update local state for immediate feedback
    setWorkflows((prev) =>
      prev.map((workflow) => workflow.id === selectedWorkflowId ? updatedWorkflow : workflow)
    );

    // Persist to backend
    await persistWorkflowUpdate(selectedWorkflowId, updatedWorkflow, `Stage "${stageToDelete.name}" deleted`);
  }, [selectedWorkflowId, workflows, setWorkflows, persistWorkflowUpdate]);

  const handleReorderStages = useCallback(async (reorderedStages: WorkflowStage[]) => {
    if (!selectedWorkflowId) return;

    const currentWorkflow = workflows.find((w) => w.id === selectedWorkflowId);
    if (!currentWorkflow) return;

    const updatedWorkflow: Workflow = {
      ...currentWorkflow,
      stages: reorderedStages,
      updatedAt: new Date(),
    };

    // Update local state for immediate feedback
    setWorkflows((prev) =>
      prev.map((workflow) => workflow.id === selectedWorkflowId ? updatedWorkflow : workflow)
    );

    // Persist to backend
    await persistWorkflowUpdate(selectedWorkflowId, updatedWorkflow, 'Stage order updated');
  }, [selectedWorkflowId, workflows, setWorkflows, persistWorkflowUpdate]);

  const handleDuplicateStageInWorkflow = useCallback(async (stageId: string) => {
    if (!selectedWorkflowId) return;

    const currentWorkflow = workflows.find((w) => w.id === selectedWorkflowId);
    if (!currentWorkflow) return;

    const stageToDuplicate = currentWorkflow.stages.find((s) => s.id === stageId);
    if (!stageToDuplicate) return;

    const stageIndex = currentWorkflow.stages.findIndex((s) => s.id === stageId);
    const tempStageId = `temp-stage-${Date.now()}-dup`;
    const duplicatedStage: WorkflowStage = {
      ...stageToDuplicate,
      id: tempStageId,
      name: `${stageToDuplicate.name} (Copy)`,
      status: 'draft' as const,
    };

    // Insert duplicated stage right after the original, then renumber all orders
    const newStages = [
      ...currentWorkflow.stages.slice(0, stageIndex + 1),
      duplicatedStage,
      ...currentWorkflow.stages.slice(stageIndex + 1),
    ].map((s, index) => ({ ...s, order: index + 1 }));

    const updatedWorkflow: Workflow = {
      ...currentWorkflow,
      stages: newStages,
      updatedAt: new Date(),
    };

    // Update local state for immediate feedback
    setWorkflows((prev) =>
      prev.map((workflow) => workflow.id === selectedWorkflowId ? updatedWorkflow : workflow)
    );

    // Persist to backend
    await persistWorkflowUpdate(selectedWorkflowId, updatedWorkflow, `Stage "${stageToDuplicate.name}" duplicated`);
  }, [selectedWorkflowId, workflows, setWorkflows, persistWorkflowUpdate]);

  return { handleDeleteStage, handleReorderStages, handleDuplicateStageInWorkflow };
};
